/**
 * Copyright (c) 2025 Roboto Technologies, Inc.
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 */

/**
 * Types of principals supported by the Roboto platform.
 */
export enum RobotoPrincipalType {
  /** See Device domain objects */
  Device = "device",
  /** A third-party integration which has been granted limited to the Roboto platform. **/
  Integration = "integration",
  /** See Invocation domain objects */
  Invocation = "invocation",
  /** See User domain objects */
  User = "user",
  /** See Org domain objects */
  Org = "org",
}

/**
 * Represents a principal (user or organization) in the Roboto platform.
 *
 * A principal is an entity that can perform actions, own resources, or be granted
 * permissions within the Roboto platform. This class provides a unified way to
 * represent both users and organizations.
 */
export class RobotoPrincipal {
  /** The type of principal (user, organization, device, or invocation). */
  public readonly ptype: RobotoPrincipalType;

  /**
   * Fully qualified principal ID.
   *
   * For resources which have universally unique IDs (e.g. users, orgs, invocations, datasets, files, etc.) this will
   * just be the resource ID.
   *
   * For resources which have org-unique IDs (e.g. devices, actions), this will be the resource ID + "@" + the org ID,
   * e.g. a device `sn001` in org `og_67890` will have a fully qualified ID of `sn001@og_67890`.
   */
  public readonly id: string;

  constructor(ptype: RobotoPrincipalType, id: string) {
    this.validateId(id);
    this.ptype = ptype;
    this.id = id;
  }

  private validateId(id: string): void {
    if (id.includes(":")) {
      throw new Error(`ID cannot contain a colon, got ${id}`);
    }
  }

  /**
   * Create a principal representing a device.
   *
   * @param deviceId - Unique identifier for the device.
   * @param orgId - Organization ID that owns the device.
   * @returns A RobotoPrincipal instance configured for the specified device.
   *
   * @example
   * ```typescript
   * const devicePrincipal = RobotoPrincipal.forDevice("robot_001", "og_abc123");
   * console.log(devicePrincipal.ptype); // RobotoPrincipalType.Device
   * console.log(devicePrincipal.id); // "robot_001@og_abc123"
   * ```
   */
  static forDevice(deviceId: string, orgId: string): RobotoPrincipal {
    return new RobotoPrincipal(
      RobotoPrincipalType.Device,
      `${deviceId}@${orgId}`,
    );
  }

  /**
   * Create a principal representing an integration.
   *
   * @param integrationId - Unique identifier for the integration.
   * @returns A RobotoPrincipal instance configured for the specified integration.
   *
   * @example
   * ```typescript
   * const integrationPrincipal = RobotoPrincipal.forIntegration("int_12345");
   * console.log(integrationPrincipal.ptype); // RobotoPrincipalType.Integration
   * console.log(integrationPrincipal.id); // "int_12345"
   * ```
   */
  static forIntegration(integrationId: string): RobotoPrincipal {
    return new RobotoPrincipal(RobotoPrincipalType.Integration, integrationId);
  }

  /**
   * Create a principal representing an invocation.
   *
   * @param invocationId - Unique identifier for the invocation.
   * @returns A RobotoPrincipal instance configured for the specified invocation.
   *
   * @example
   * ```typescript
   * const invocationPrincipal = RobotoPrincipal.forInvocation("iv_12345");
   * console.log(invocationPrincipal.ptype); // RobotoPrincipalType.Invocation
   * console.log(invocationPrincipal.id); // "iv_12345"
   * ```
   */
  static forInvocation(invocationId: string): RobotoPrincipal {
    return new RobotoPrincipal(RobotoPrincipalType.Invocation, invocationId);
  }

  /**
   * Create a principal representing an organization.
   *
   * @param orgId - Unique identifier for the organization.
   * @returns A RobotoPrincipal instance configured for the specified organization.
   *
   * @example
   * ```typescript
   * const orgPrincipal = RobotoPrincipal.forOrg("org_67890");
   * console.log(orgPrincipal.ptype); // RobotoPrincipalType.Org
   * console.log(orgPrincipal.id); // "org_67890"
   * ```
   */
  static forOrg(orgId: string): RobotoPrincipal {
    return new RobotoPrincipal(RobotoPrincipalType.Org, orgId);
  }

  /**
   * Create a principal representing a user.
   *
   * @param userId - Unique identifier for the user.
   * @returns A RobotoPrincipal instance configured for the specified user.
   *
   * @example
   * ```typescript
   * const userPrincipal = RobotoPrincipal.forUser("user_12345");
   * console.log(userPrincipal.ptype); // RobotoPrincipalType.User
   * console.log(userPrincipal.id); // "user_12345"
   * ```
   */
  static forUser(userId: string): RobotoPrincipal {
    return new RobotoPrincipal(RobotoPrincipalType.User, userId);
  }

  /**
   * Create a principal from a serialized string representation.
   *
   * @param serializedPrincipal - String in format "ptype:id"
   * @returns A RobotoPrincipal instance parsed from the string, or null if the string is not in the correct format.
   */
  static fromString(serializedPrincipal: string): RobotoPrincipal | null {
    const parts = serializedPrincipal.split(":");
    if (parts.length !== 2) {
      return null;
    }

    const [ptypeStr, id] = parts;
    const ptype = ptypeStr as RobotoPrincipalType;

    if (!Object.values(RobotoPrincipalType).includes(ptype)) {
      return null;
    }

    return new RobotoPrincipal(ptype, id);
  }

  /**
   * Convert the principal to its string representation.
   *
   * @returns String in format "ptype:id"
   *
   * @example
   * ```typescript
   * const principal = RobotoPrincipal.forUser("user_12345");
   * console.log(principal.toString()); // "user:user_12345"
   * ```
   */
  toString(): string {
    return `${this.ptype}:${this.id}`;
  }

  get unqualifiedId(): string {
    if (this.ptype === RobotoPrincipalType.User) {
      return this.id;
    }

    return this.id.split("@")[0];
  }
}
